/*
  Native product listing (snippets/ia-plp.liquid).
  Every filter, sort and stock change becomes a URL; the section is fetched for that URL
  through the Section Rendering API and its parts are swapped in place. The listing
  element itself stays, so its listeners survive every swap.
*/
(function () {
  if (customElements.get('ia-plp')) return;

  var PRICE_DEBOUNCE_MS = 500;
  var PREFETCH_FRESH_MS = 120000;

  // Added on connect, removed on disconnect: a section the editor moves keeps one of each
  var OWN_EVENTS = [
    ['click', 'onClick'],
    ['change', 'onChange'],
    ['submit', 'onSubmit'],
    ['keydown', 'onKeydown'],
    ['input', 'onInput'],
    ['pointerover', 'onSwatchIntent'],
    ['focusin', 'onSwatchIntent'],
  ];

  class IaPlp extends HTMLElement {
    constructor() {
      super();
      var self = this;
      ['onClick', 'onChange', 'onSubmit', 'onKeydown', 'onInput', 'onSwatchIntent', 'onPopstate', 'onOutsideClick', 'onToggle'].forEach(
        function (name) {
          self[name] = self[name].bind(self);
        }
      );
    }

    connectedCallback() {
      var self = this;
      this.sectionId = this.dataset.sectionId;
      this.requestId = 0;
      this.controller = null;
      this.priceTimer = null;
      this.pageController = null;
      this.rendering = false;
      this.prefetched = null;
      this.prefetchTimer = null;
      this.renderedUrl = window.location.pathname + window.location.search;
      // Unlocks the drawer transitions in ia-plp.css
      this.classList.add('ia-plp--ready');

      OWN_EVENTS.forEach(function (pair) {
        self.addEventListener(pair[0], self[pair[1]]);
      });
      // toggle does not bubble; the capture phase still sees it on the groups
      this.addEventListener('toggle', this.onToggle, true);
      window.addEventListener('popstate', this.onPopstate);
      this.watchMore();
      this.syncTwins();
      this.chipsFromAddress();
      this.schedulePrefetch();
    }

    disconnectedCallback() {
      var self = this;
      this.classList.remove('ia-plp--ready');
      OWN_EVENTS.forEach(function (pair) {
        self.removeEventListener(pair[0], self[pair[1]]);
      });
      this.removeEventListener('toggle', this.onToggle, true);
      window.removeEventListener('popstate', this.onPopstate);
      document.removeEventListener('click', this.onOutsideClick);
      if (this.controller) this.controller.abort();
      if (this.pageController) this.pageController.abort();
      if (this.moreObserver) this.moreObserver.disconnect();
      clearTimeout(this.priceTimer);
      this.cancelPrefetch();
      // A drawer left open would keep the page scroll locked
      if (this.classList.contains('ia-plp--drawer-open')) {
        this.classList.remove('ia-plp--drawer-open');
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
        if (window.theme && window.theme.a11y) window.theme.a11y.removeTrapFocus();
      }
    }

    onToggle(event) {
      var group = event.target;
      if (group.classList && group.classList.contains('ia-plp__group')) {
        group.classList.toggle('ia-plp__group--open', group.open);
      }
    }

    // Auto mode: the button presses itself this far below the viewport and stays a real button
    watchMore() {
      if (!this.hasAttribute('data-auto-load') || !('IntersectionObserver' in window)) return;
      var self = this;
      if (!this.moreObserver) {
        this.moreObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting && !self.pageController && !self.rendering) self.loadPage(entry.target, true);
            });
          },
          {rootMargin: '0px 0px ' + (parseInt(this.getAttribute('data-auto-load'), 10) || 1200) + 'px 0px'}
        );
      }
      this.moreObserver.disconnect();
      var next = this.querySelector('[data-ia-plp-next]');
      if (next) this.moreObserver.observe(next);
    }

    get form() {
      return this.querySelector('[data-ia-plp-form]');
    }

    get drawer() {
      return this.querySelector('[data-ia-plp-drawer]');
    }

    // Drawer

    openDrawer() {
      var drawer = this.drawer;
      var button = this.querySelector('[data-ia-plp-open]');
      if (!drawer) return;
      drawer.removeAttribute('inert');
      this.classList.add('ia-plp--drawer-open');
      if (button) button.setAttribute('aria-expanded', 'true');
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));
      if (window.theme && window.theme.a11y) {
        // The dialog itself takes focus, so no control opens with a focus ring on it
        window.theme.a11y.trapFocus(drawer, {elementToFocus: drawer});
      }
    }

    closeDrawer() {
      var drawer = this.drawer;
      var button = this.querySelector('[data-ia-plp-open]');
      if (!drawer || !this.classList.contains('ia-plp--drawer-open')) return;
      this.classList.remove('ia-plp--drawer-open');
      drawer.setAttribute('inert', '');
      if (button) button.setAttribute('aria-expanded', 'false');
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
      if (window.theme && window.theme.a11y) window.theme.a11y.removeTrapFocus();
      if (button) button.focus();
    }

    // Sort menu

    toggleSort(open) {
      var button = this.querySelector('[data-ia-plp-sort-toggle]');
      var list = this.querySelector('.ia-plp__sort-list');
      if (!button || !list) return;
      var willOpen = typeof open === 'boolean' ? open : button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(willOpen));
      button.classList.toggle('ia-plp__sort-btn--open', willOpen);
      list.hidden = !willOpen;
      if (willOpen) {
        document.addEventListener('click', this.onOutsideClick);
      } else {
        document.removeEventListener('click', this.onOutsideClick);
      }
    }

    onOutsideClick(event) {
      var sort = this.querySelector('[data-ia-plp-sort]');
      if (sort && !sort.contains(event.target)) this.toggleSort(false);
    }

    setSort(value) {
      var form = this.form;
      if (!form) return;
      var input = form.querySelector('[data-ia-plp-sort-input]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'sort_by';
        input.setAttribute('data-ia-plp-sort-input', '');
        form.prepend(input);
      }
      input.value = value;
      this.toggleSort(false);
      this.apply();
    }

    // Events

    onClick(event) {
      var target = event.target;
      var hit;

      if ((hit = target.closest('.custom_collection_swatches a.swatch'))) {
        event.preventDefault();
        // The theme-wide jQuery swatch handler in custom.js writes one URL into srcset;
        // here the card keeps its responsive candidates
        event.stopPropagation();
        this.selectSwatch(hit);
      } else if ((hit = target.closest('[data-ia-plp-open]'))) {
        event.preventDefault();
        this.openDrawer();
      } else if ((hit = target.closest('[data-ia-plp-close], [data-ia-plp-show]'))) {
        event.preventDefault();
        if (this.priceTimer) {
          // A price typed a moment ago is still waiting for its debounce
          clearTimeout(this.priceTimer);
          this.priceTimer = null;
          this.apply();
        }
        this.closeDrawer();
      } else if ((hit = target.closest('[data-ia-plp-link], [data-ia-plp-clear]'))) {
        event.preventDefault();
        this.navigate(hit.getAttribute('href'));
        if (hit.hasAttribute('data-ia-plp-clear') && this.hasAttribute('data-clear-closes')) this.closeDrawer();
      } else if ((hit = target.closest('[data-ia-plp-stock]'))) {
        event.preventDefault();
        this.toggleStock(hit);
      } else if ((hit = target.closest('[data-ia-plp-sort-toggle]'))) {
        event.preventDefault();
        this.toggleSort();
      } else if ((hit = target.closest('[data-ia-plp-sort-value]'))) {
        event.preventDefault();
        this.setSort(hit.getAttribute('data-ia-plp-sort-value'));
      } else if ((hit = target.closest('[data-ia-plp-next], [data-ia-plp-prev]'))) {
        event.preventDefault();
        this.loadPage(hit);
      }
    }

    onChange(event) {
      var target = event.target;
      if (!this.form || !this.form.contains(target)) return;
      if (target.closest('[data-ia-plp-price]')) {
        this.debounceApply();
        return;
      }
      if (target.type === 'checkbox' && target.checked) this.keepSingle(target);
      var key = target.getAttribute('data-ia-plp-twin-key');
      var list = target.closest('ul');
      if (key && list) {
        list.querySelectorAll('[data-ia-plp-twin]').forEach(function (twin) {
          if (twin.getAttribute('data-ia-plp-twin') === key) twin.checked = target.checked;
        });
      }
      this.apply();
    }

    onInput(event) {
      var block = event.target.closest && event.target.closest('[data-ia-plp-price]');
      if (block) this.syncPrice(block, event.target);
    }

    // Keeps the fields, the thumbs, the black line and the text on one range
    syncPrice(block, source) {
      var minField = block.querySelector('[data-ia-plp-price-min]');
      var maxField = block.querySelector('[data-ia-plp-price-max]');
      var minRange = block.querySelector('[data-ia-plp-range-min]');
      var maxRange = block.querySelector('[data-ia-plp-range-max]');
      var line = block.querySelector('[data-ia-plp-price-line]');
      var display = block.querySelector('[data-ia-plp-price-display]');
      if (!minField || !maxField || !minRange || !maxRange) return;
      var top = Number(maxRange.max) || 0;
      var clamp = function (value, fallback) {
        var number = value === '' || isNaN(Number(value)) ? fallback : Number(value);
        return Math.min(Math.max(number, 0), top);
      };
      var low;
      var high;
      if (source === minRange || source === maxRange) {
        low = Number(minRange.value);
        high = Number(maxRange.value);
        if (low > high) {
          if (source === minRange) low = high;
          else high = low;
        }
        minRange.value = low;
        maxRange.value = high;
        minField.value = low;
        maxField.value = high;
      } else {
        low = clamp(minField.value, 0);
        high = clamp(maxField.value, top);
        minRange.value = Math.min(low, high);
        maxRange.value = Math.max(low, high);
      }
      var symbol = block.getAttribute('data-symbol') || '';
      if (line && top > 0) {
        line.style.left = (Math.min(low, high) / top) * 100 + '%';
        line.style.right = 100 - (Math.max(low, high) / top) * 100 + '%';
      }
      if (display) display.textContent = symbol + Math.min(low, high) + ' - ' + symbol + Math.max(low, high);
    }

    debounceApply() {
      var self = this;
      clearTimeout(this.priceTimer);
      this.priceTimer = setTimeout(function () {
        self.priceTimer = null;
        self.apply();
      }, PRICE_DEBOUNCE_MS);
    }

    onSubmit(event) {
      if (!this.form || event.target !== this.form) return;
      event.preventDefault();
      this.closeDrawer();
      this.apply();
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      this.toggleSort(false);
      this.closeDrawer();
    }

    // Only a change of the query is ours to answer; a hash change is not
    onPopstate() {
      if (window.location.pathname + window.location.search === this.renderedUrl) return;
      this.render(window.location.href);
    }

    toggleStock(button) {
      var form = this.form;
      if (!form) return;
      var on = this.flipStock(form, button);
      button.setAttribute('aria-checked', String(on));
      button.classList.toggle('ia-plp__stock--on', on);
      this.apply();
    }

    // One path flips the stock value for the live form and the prefetch's copy, so both build one URL
    flipStock(form, button) {
      var selector =
        'input[name="' + CSS.escape(button.dataset.param) + '"][value="' + CSS.escape(button.dataset.value) + '"]';
      var input = form.querySelector(selector);
      var on;
      if (input && input.type === 'checkbox') {
        input.checked = !input.checked;
        on = input.checked;
        if (on) this.keepSingle(input, form);
      } else if (input) {
        // Active, but its group is hidden in the drawer: the value rides as a hidden field
        input.remove();
        on = false;
      } else {
        // Its group is hidden in the drawer, so there is no box to tick. The field goes into
        // the groups, which the next response replaces with the server's own state.
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = button.dataset.param;
        hidden.value = button.dataset.value;
        (form.querySelector('[data-ia-plp-groups]') || form).append(hidden);
        this.keepSingle(hidden, form);
        on = true;
      }
      return on;
    }

    // A one-choice filter (Theme settings) keeps only the value just picked, with its twins
    keepSingle(field, form) {
      form = form || this.form;
      var single = form ? form.getAttribute('data-ia-plp-single') || '' : '';
      if (!field.name || single.indexOf('|' + field.name + '|') === -1) return;
      var twinKey = field.getAttribute('data-ia-plp-twin-key');
      Array.prototype.slice.call(form.elements).forEach(function (other) {
        if (other === field || other.name !== field.name || other.value === field.value) return;
        if (twinKey && other.getAttribute('data-ia-plp-twin') === twinKey) return;
        if (other.type === 'checkbox') other.checked = false;
        else if (other.type === 'hidden') other.remove();
      });
    }

    // Requests

    paramsFromForm(form) {
      form = form || this.form;
      var params = new URLSearchParams();
      if (!form) return params;
      Array.prototype.forEach.call(form.elements, function (field) {
        if (!field.name || field.disabled || field.value === '') return;
        if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
        // A price field on its bound asks for nothing
        if (field.hasAttribute('data-bound') && Number(field.value) === Number(field.getAttribute('data-bound'))) return;
        params.append(field.name, field.value);
      });
      return params;
    }

    // A value that differs only in case rides hidden with its visible twin; the visible
    // box shows ticked when any of them is active
    syncTwins() {
      this.querySelectorAll('[data-ia-plp-twin]:checked').forEach(function (twin) {
        var list = twin.closest('ul');
        var key = twin.getAttribute('data-ia-plp-twin');
        if (!list) return;
        list.querySelectorAll('[data-ia-plp-twin-key]').forEach(function (box) {
          if (box.getAttribute('data-ia-plp-twin-key') === key) box.checked = true;
        });
      });
    }

    // Empty results: Shopify reports no active list values, so the chips and the form
    // would forget them. The page sends the labels; the address says what is active.
    chipsFromAddress() {
      var wrap = this.querySelector('[data-ia-plp-selected]');
      var data = wrap && wrap.querySelector('[data-ia-plp-labels]');
      var form = this.form;
      if (!data || !form || wrap.hasAttribute('data-ia-plp-built')) return;
      wrap.setAttribute('data-ia-plp-built', '');
      var info;
      try {
        info = JSON.parse(data.textContent);
      } catch {
        return;
      }
      var address = new URL(this.renderedUrl, window.location.origin);
      var params = address.searchParams;
      var pairs = [];
      params.forEach(function (value, key) {
        if (key.indexOf('filter.') === 0 && key.indexOf('filter.v.price.') !== 0) pairs.push([key, value]);
      });
      if (!pairs.length) return;

      var carry = form.querySelector('[data-ia-plp-groups]') || form;
      var list = wrap.querySelector('.ia-plp__selected');
      var clearItem = list && list.querySelector('.ia-plp__chip--clear');
      if (!list) {
        list = document.createElement('ul');
        list.className = 'ia-plp__selected';
        list.setAttribute('role', 'list');
        wrap.appendChild(list);
      }
      var decode = document.createElement('textarea');
      var icon = wrap.ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'svg');
      icon.setAttribute('class', 'ia-plp__chip-icon');
      icon.setAttribute('viewBox', '0 0 11 10');
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = '<path d="M1 .5l9 9m0-9l-9 9" stroke="currentColor" fill="none"/>';

      var item = function (href, text, extraClass, withIcon) {
        var li = document.createElement('li');
        li.className = 'ia-plp__selected-item';
        var a = document.createElement('a');
        a.className = 'ia-plp__chip' + (extraClass ? ' ' + extraClass : '');
        a.href = href;
        a.setAttribute(extraClass ? 'data-ia-plp-clear' : 'data-ia-plp-link', '');
        a.textContent = text;
        if (withIcon) a.appendChild(icon.cloneNode(true));
        li.appendChild(a);
        return li;
      };

      pairs.forEach(function (pair) {
        var selector = 'input[name="' + CSS.escape(pair[0]) + '"][value="' + CSS.escape(pair[1]) + '"]';
        if (!form.querySelector(selector)) {
          var hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = pair[0];
          hidden.value = pair[1];
          carry.appendChild(hidden);
        }
        var rest = new URLSearchParams();
        params.forEach(function (value, key) {
          if (key !== 'page' && !(key === pair[0] && value === pair[1])) rest.append(key, value);
        });
        var query = rest.toString();
        decode.innerHTML = info.labels[pair[0] + '|' + pair[1]] || pair[1];
        var chip = item(address.pathname + (query ? '?' + query : ''), decode.value, '', true);
        if (clearItem) list.insertBefore(chip, clearItem.parentNode);
        else list.appendChild(chip);
      });
      if (!clearItem) list.appendChild(item(info.clear, info.clearText, 'ia-plp__chip--clear', false));
    }

    navigate(href) {
      var url = new URL(href, window.location.origin);
      this.go(url.pathname + url.search);
    }

    apply() {
      this.go(this.listingUrl(this.paramsFromForm()));
    }

    // Analytics pixels listen to pushState and cost tens of ms each time: with the response
    // in hand the grid paints first, while a request is in flight the address changes at once
    go(url) {
      var ready = this.render(url);
      if (url === window.location.pathname + window.location.search) return;
      var push = function () {
        window.history.pushState({iaPlp: true}, '', url);
      };
      if (ready) {
        requestAnimationFrame(function () {
          setTimeout(push, 0);
        });
      } else {
        push();
      }
    }

    // The address for a set of form fields; parameters the listing does not own (utm_*, q) stay
    listingUrl(formParams) {
      var params = new URLSearchParams();
      new URLSearchParams(window.location.search).forEach(function (value, key) {
        if (key.indexOf('filter.') !== 0 && key !== 'sort_by' && key !== 'page') params.append(key, value);
      });
      formParams.forEach(function (value, key) {
        params.append(key, value);
      });
      var query = params.toString();
      return window.location.pathname + (query ? '?' + query : '');
    }

    sectionRequest(url, signal) {
      var fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      return fetch(fetchUrl.toString(), {signal: signal, credentials: 'same-origin'}).then(function (response) {
        if (!response.ok) throw new Error('Listing request failed: ' + response.status);
        return response.text();
      });
    }

    // The stock switch has two states: the other one is fetched once the listing settles, a click takes it
    schedulePrefetch() {
      this.cancelPrefetch();
      if (!this.querySelector('[data-ia-plp-stock]') || !this.form) return;
      if (navigator.connection && navigator.connection.saveData) return;
      this.prefetchTimer = setTimeout(this.prefetchStock.bind(this), 1500);
    }

    prefetchStock() {
      this.prefetchTimer = null;
      var button = this.querySelector('[data-ia-plp-stock]');
      if (!button || !this.form || this.rendering) return;
      var copy = this.form.cloneNode(true);
      this.flipStock(copy, button);
      var url = this.listingUrl(this.paramsFromForm(copy));
      var controller = new AbortController();
      var request = this.sectionRequest(url, controller.signal);
      // Nobody may be waiting on it; a failure only means the click fetches for itself
      request.catch(function () {});
      this.prefetched = {url: url, at: Date.now(), controller: controller, request: request};
    }

    cancelPrefetch() {
      clearTimeout(this.prefetchTimer);
      this.prefetchTimer = null;
      if (this.prefetched) this.prefetched.controller.abort();
      this.prefetched = null;
    }

    // Says whether the response was already in hand (the prefetched stock state)
    render(url) {
      var self = this;
      var id = ++this.requestId;
      if (this.controller) this.controller.abort();
      if (this.pageController) this.pageController.abort();
      // No page of the old list may land in the new one, nor a pending price edit push its URL
      this.rendering = true;
      if (this.moreObserver) this.moreObserver.disconnect();
      clearTimeout(this.priceTimer);
      this.priceTimer = null;

      var target = new URL(url, window.location.origin);
      this.renderedUrl = target.pathname + target.search;

      var request;
      var prefetched = this.prefetched;
      this.prefetched = null;
      clearTimeout(this.prefetchTimer);
      var ready = false;
      if (prefetched && prefetched.url === url && Date.now() - prefetched.at < PREFETCH_FRESH_MS) {
        this.controller = prefetched.controller;
        request = prefetched.request;
        ready = true;
      } else {
        if (prefetched) prefetched.controller.abort();
        this.controller = new AbortController();
        request = this.sectionRequest(url, this.controller.signal);
      }

      this.setBusy(true);
      request
        .then(function (html) {
          if (id !== self.requestId) return;
          var next = new DOMParser().parseFromString(html, 'text/html').querySelector('ia-plp');
          if (!next) throw new Error('Listing missing in the section response');
          self.swap(next);
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          // The page itself still answers the same URL
          window.location.assign(url);
        })
        .finally(function () {
          if (id !== self.requestId) return;
          self.setBusy(false);
          self.rendering = false;
          self.watchMore();
          self.schedulePrefetch();
        });
      return ready;
    }

    setBusy(busy) {
      var results = this.querySelector('[data-ia-plp-results]');
      this.classList.toggle('ia-plp--loading', busy);
      if (results) results.setAttribute('aria-busy', String(busy));
    }

    // Swaps the parts that depend on the results; the drawer keeps its open groups,
    // its scroll and the focused control
    swap(next) {
      var self = this;

      var openGroups = [];
      this.querySelectorAll('[data-ia-plp-group][open]').forEach(function (group) {
        openGroups.push(group.getAttribute('data-ia-plp-group'));
      });
      var groups = this.querySelector('[data-ia-plp-groups]');
      var scrollTop = groups ? groups.scrollTop : 0;
      var active = document.activeElement;
      var focusedId = active && this.contains(active) ? active.id : '';
      var focusWasInDrawer = !!(active && this.drawer && this.drawer.contains(active));
      var focusWasInSort = !!(active && active.closest && active.closest('[data-ia-plp-sort]'));
      this.toggleSort(false);

      ['[data-ia-plp-toolbar]', '[data-ia-plp-selected]', '[data-ia-plp-groups]', '[data-ia-plp-footer]'].forEach(function (selector) {
        var current = self.querySelector(selector);
        var incoming = next.querySelector(selector);
        if (current && incoming) current.replaceWith(incoming);
      });
      this.swapResults(next);
      this.syncTwins();
      this.chipsFromAddress();

      var sortInput = next.querySelector('[data-ia-plp-sort-input]');
      var form = this.form;
      var currentSortInput = form && form.querySelector('[data-ia-plp-sort-input]');
      if (currentSortInput) currentSortInput.remove();
      if (form && sortInput) form.prepend(sortInput);

      openGroups.forEach(function (key) {
        var group = self.querySelector('[data-ia-plp-group="' + CSS.escape(key) + '"]');
        if (group) group.open = true;
      });
      var newGroups = this.querySelector('[data-ia-plp-groups]');
      if (newGroups) newGroups.scrollTop = scrollTop;
      // A chip or option that is gone hands focus to the drawer or the sort button, not the body
      var focusTarget = focusedId ? document.getElementById(focusedId) : null;
      if (!focusTarget && focusWasInDrawer) focusTarget = this.drawer;
      if (!focusTarget && focusWasInSort) focusTarget = this.querySelector('[data-ia-plp-sort-toggle]');
      if (focusTarget) focusTarget.focus({preventScroll: true});

      var drawerOpen = this.classList.contains('ia-plp--drawer-open');
      var openButton = this.querySelector('[data-ia-plp-open]');
      if (openButton && drawerOpen) openButton.setAttribute('aria-expanded', 'true');
      // The trap remembers its first and last controls, and the footer was just replaced
      if (drawerOpen && window.theme && window.theme.a11y) {
        window.theme.a11y.trapFocus(this.drawer, {elementToFocus: focusTarget || this.drawer});
      }

      this.announce();
    }

    // The results arrive whole, but a card that did not change keeps its node, so its
    // image neither reloads nor replays the theme's reveal; the rest come in settled
    swapResults(next) {
      var current = this.querySelector('[data-ia-plp-results]');
      var incoming = next.querySelector('[data-ia-plp-results]');
      if (!current || !incoming) return;
      var grid = current.querySelector('[data-ia-plp-grid]');
      var incomingGrid = incoming.querySelector('[data-ia-plp-grid]');
      if (grid && incomingGrid) {
        var kept = {};
        Array.prototype.forEach.call(grid.children, function (card) {
          if (card.id) kept[cardKey(card)] = card;
        });
        var children = Array.prototype.map.call(incomingGrid.children, function (card) {
          var same = card.id && kept[cardKey(card)];
          if (same) return same;
          settle(card);
          return card;
        });
        grid.replaceChildren.apply(grid, children);
        incomingGrid.replaceWith(grid);
      } else {
        settle(incoming);
      }
      current.replaceWith(incoming);
    }

    // Card swatches: the chosen metal's variant takes over the image, the links and the
    // label, with no request

    selectSwatch(swatch) {
      var card = swatch.closest('.product-item');
      var group = swatch.closest('.custom_collection_swatches');
      if (!card || !group) return;

      group.querySelectorAll('a.swatch').forEach(function (item) {
        var on = item === swatch;
        item.classList.toggle('selected-swatch', on);
        if (on) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });

      var label = card.querySelector('.custom_option_title');
      if (label) label.textContent = swatch.getAttribute('data-title') || '';

      var href = swatch.getAttribute('href');
      card.querySelectorAll('a.product-link').forEach(function (link) {
        link.setAttribute('href', href);
        link.setAttribute('data-product-link', href);
      });

      // A metal with its own family price carries it; the others show the price the page came with
      var priceBox = card.querySelector('[data-product-price]');
      if (priceBox) {
        if (priceBox.iaPlpDefaultPrice === undefined) priceBox.iaPlpDefaultPrice = priceBox.innerHTML;
        var ownPrice = swatch.querySelector('template[data-ia-plp-swatch-price]');
        priceBox.innerHTML = ownPrice ? ownPrice.innerHTML : priceBox.iaPlpDefaultPrice;
      }

      var variantId = swatch.getAttribute('data-variant-id');
      if (variantId) {
        card.querySelectorAll('input[name="id"]').forEach(function (input) {
          input.value = variantId;
        });
      }

      var img = card.querySelector('[data-product-image-default] img');
      var target = swatch.getAttribute('data-image-src');
      if (img && target) {
        var next = this.sourcesFor(img, target);
        if (next.srcset) img.srcset = next.srcset;
        if (next.src) img.src = next.src;
      }
    }

    // The card image's own candidates pointed at another file: width parameters and
    // descriptors stay, so the browser picks the same size it already chose
    sourcesFor(img, target) {
      var targetUrl;
      try {
        targetUrl = new URL(target, window.location.href);
      } catch {
        return {src: target};
      }
      var swap = function (url) {
        try {
          var next = new URL(url, window.location.href);
          next.pathname = targetUrl.pathname;
          var version = targetUrl.searchParams.get('v');
          if (version) next.searchParams.set('v', version);
          else next.searchParams.delete('v');
          if (/^https?:/.test(url)) return next.toString();
          if (url.indexOf('//') === 0) return '//' + next.host + next.pathname + next.search;
          return next.pathname + next.search;
        } catch {
          return target;
        }
      };
      var srcset = img.getAttribute('srcset');
      return {
        srcset: srcset
          ? srcset
              .split(',')
              .map(function (candidate) {
                var bits = candidate.trim().split(/\s+/);
                return swap(bits[0]) + (bits[1] ? ' ' + bits[1] : '');
              })
              .join(', ')
          : '',
        src: img.getAttribute('src') ? swap(img.getAttribute('src')) : target,
      };
    }

    // Warms the image of a swatch the pointer or focus reaches, once
    onSwatchIntent(event) {
      var swatch = event.target.closest && event.target.closest('.custom_collection_swatches a.swatch');
      if (!swatch || swatch.hasAttribute('data-ia-plp-warmed')) return;
      var card = swatch.closest('.product-item');
      var img = card && card.querySelector('[data-product-image-default] img');
      var target = swatch.getAttribute('data-image-src');
      if (!img || !target) return;
      swatch.setAttribute('data-ia-plp-warmed', '');
      var next = this.sourcesFor(img, target);
      var warm = new Image();
      warm.decoding = 'async';
      if (img.sizes) warm.sizes = img.sizes;
      if (next.srcset) warm.srcset = next.srcset;
      warm.src = next.src;
    }

    // Load more / Show previous: the next or previous page of the same URL, cards merged
    // into the grid. The address stays as it is; the pager inside the page keeps page N
    // reachable for crawlers and for anyone without the script.
    loadPage(link, auto) {
      var self = this;
      if (this.pageController || this.rendering) return;
      var before = link.hasAttribute('data-ia-plp-prev');
      var hadFocus = document.activeElement === link;
      var listingRequest = this.requestId;
      var fetchUrl = new URL(link.href, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      this.pageController = new AbortController();
      link.setAttribute('aria-busy', 'true');
      link.classList.add('is-loading');

      fetch(fetchUrl.toString(), {signal: this.pageController.signal, credentials: 'same-origin'})
        .then(function (response) {
          if (!response.ok) throw new Error('Page request failed: ' + response.status);
          return response.text();
        })
        .then(function (html) {
          // A filter change replaced the list meanwhile; this page belongs to the old one
          if (listingRequest !== self.requestId || !link.isConnected) return;
          var next = new DOMParser().parseFromString(html, 'text/html').querySelector('ia-plp');
          if (!next) throw new Error('Listing missing in the section response');
          var added = self.mergePage(next, before);
          self.announceAdded(added.length);
          if (!before) self.watchMore();
          if (hadFocus) {
            var holder = self.querySelector(before ? '[data-ia-plp-prev]' : '[data-ia-plp-next]');
            var firstLink = added[0] && added[0].querySelector('a[href]');
            if (holder) holder.focus({preventScroll: true});
            else if (firstLink) firstLink.focus({preventScroll: true});
          }
        })
        .catch(function (error) {
          // A press falls back to the page itself; a scroll leaves the button to press
          if (error.name === 'AbortError' || auto) return;
          window.location.assign(link.href);
        })
        .finally(function () {
          self.pageController = null;
          link.removeAttribute('aria-busy');
          link.classList.remove('is-loading');
        });
    }

    mergePage(next, before) {
      var grid = this.querySelector('[data-ia-plp-grid]');
      var incoming = next.querySelector('[data-ia-plp-grid]');
      if (!grid || !incoming) return [];

      // A card already on screen (a product moved between pages) is not added twice
      var added = Array.prototype.filter.call(incoming.children, function (card) {
        return !(card.id && document.getElementById(card.id));
      });
      // Appended pages arrive settled and lazy: first row priority belongs to the landing page
      added.forEach(function (card) {
        settle(card);
        card.querySelectorAll('img').forEach(function (img) {
          img.loading = 'lazy';
          img.removeAttribute('fetchpriority');
        });
      });
      if (before) {
        grid.prepend.apply(grid, added);
      } else {
        grid.append.apply(grid, added);
      }

      var holderSelector = before ? '[data-ia-plp-more-prev]' : '[data-ia-plp-more-next]';
      var holder = this.querySelector(holderSelector);
      var nextHolder = next.querySelector(holderSelector);
      if (holder) {
        if (nextHolder) holder.replaceWith(nextHolder);
        else holder.remove();
      }

      if (!before) {
        var pager = this.querySelector('[data-ia-plp-pager]');
        var nextPager = next.querySelector('[data-ia-plp-pager]');
        if (pager && nextPager) pager.replaceWith(nextPager);
      }
      return added;
    }

    announceAdded(count) {
      var status = this.querySelector('[data-ia-plp-status]');
      var template = this.getAttribute('data-added-text') || '[count] more products';
      if (status && count) status.textContent = template.replace('[count]', count);
    }

    announce() {
      var status = this.querySelector('[data-ia-plp-status]');
      var show = this.querySelector('[data-ia-plp-show]');
      if (status && show) status.textContent = show.textContent.trim();
    }
  }

  // What a card shows: product, link, image and the server's signature of its variant and swatches.
  // Search tracking parameters change with every response and do not count.
  function cardKey(card) {
    var link = card.querySelector('a.product-link');
    var img = card.querySelector('[data-product-image-default] img');
    var href = link ? link.getAttribute('href').replace(/[?&](_pos|_fid|_ss)=[^&]*/g, '') : '';
    return (
      card.id + '|' + href + '|' + (img ? img.getAttribute('src') : '') + '|' + (card.getAttribute('data-ia-plp-sig') || '')
    );
  }

  // Marks the theme's reveal animation as done, so swapped content does not fade in again
  function settle(root) {
    if (root.matches && root.matches('[data-aos]')) root.classList.add('aos-animate');
    root.querySelectorAll('[data-aos]').forEach(function (el) {
      el.classList.add('aos-animate');
    });
  }

  customElements.define('ia-plp', IaPlp);
})();
